import type Vue from 'vue';
import { Component } from 'vue';
import ConfirmModal from '../components/modals/ConfirmModal.vue';
import { vueData } from '../VueData';
import InputModal from '../components/modals/InputModal.vue';

export interface IDynamicModalEntry {
  modalAttrs?: { name?: string };
}

/** vue-js-modal registers its <modals-container> on the root instance. */
export type TRootWithDynamicModals = Vue & {
  _dynamicContainer?: { modals: IDynamicModalEntry[] };
};

export function showScrollableModal({
  modalInstance,
  component,
  props,
  events,
  width,
  name = 'scrollable',
  clickToClose = true,
}: {
  modalInstance: any;
  component: Component;
  props?: { [p: string]: any };
  events?: { [p: string]: (any) => any };
  width?: number;
  name?: string;
  clickToClose?: boolean;
}) {
  modalInstance.show(
    component,
    props,
    {
      name,
      classes: 'dialog--height-limit',
      adaptive: true,
      height: 'auto',
      scrollable: true,
      maxWidth: window.innerWidth * 0.95,
      width,
      clickToClose,
    },
    events
  );
}

/**
 * Promise-based dynamic dialog on an explicit vue-js-modal instance (`$modal`).
 * Same contract as useModal().showDynamicDialog — which delegates here — but
 * callable from class components and plain modules. Resolves with the value
 * passed to the dialog's `submit` event, or null when dismissed.
 */
export function showDynamicDialogOn<T>(
  modalInstance,
  component: Component,
  props,
  name = 'modal',
  options: Record<string, unknown> = {}
): Promise<T> {
  // A name passed via options must stay in sync with the hide() call below.
  const dialogName = typeof options.name === 'string' ? options.name : name;

  return new Promise<T>((resolve) => {
    modalInstance.show(
      component,
      props,
      {
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        classes: 'dialog--height-limit',
        ...options,
        name: dialogName,
      },
      {
        submit: async (value: any) => {
          modalInstance.hide(dialogName);
          resolve(value);
        },
        closed: async () => {
          resolve(null);
        },
      }
    );
  });
}

let confirmDialogId = 0;
export function showConfirmModal(
  modalInstance,
  title: string,
  message: string,
  acceptText?: string,
  cancelText?: string,
  acceptColor?: string
) {
  return new Promise<boolean>((resolve) => {
    const name = 'confirm-modal-' + ++confirmDialogId;

    modalInstance.show(
      ConfirmModal,
      {
        title,
        message,
        acceptText,
        cancelText,
        acceptColor,
      },
      {
        name,
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        confirm: () => {
          resolve(true);
          modalInstance.hide(name);
        },
        closed: () => {
          resolve(false);
        },
      }
    );
  });
}

export async function showInputModal(
  modalInstance,
  title: string,
  message: string,
  rules
) {
  return new Promise((resolve, reject) => {
    modalInstance.show(
      InputModal,
      {
        title,
        message,
        rules,
      },
      {
        name: 'custt',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        submit: (input: string) => {
          modalInstance.hide('custt');
          resolve(input);
        },
        closed: () => {
          reject();
        },
      }
    );
  });
}

export async function showAsyncInputModal(
  modalInstance,
  title: string,
  message: string,
  cb: (input: string) => Promise<boolean>,
  rules?
) {
  return new Promise((resolve) => {
    modalInstance.show(
      InputModal,
      {
        title,
        message,
        rules,
      },
      {
        name: 'custt',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        submit: async (input: string) => {
          if (await cb(input)) {
            modalInstance.hide('custt');
            resolve(true);
          }
        },
        closed: () => {
          resolve(false);
        },
      }
    );
  });
}

/**
 * Closes every open dynamic modal through `$modal.hide(name)`, so each one
 * runs its normal close lifecycle (`before-close`, `closed`): promise-based
 * dialogs settle as dismissed and `closed` handlers roll back optimistic
 * state. `$modal.hideAll()` would drop them silently and leave those
 * promises pending forever. A `before-close` listener that cancels the
 * event keeps its modal open (none exists in this codebase).
 */
export function closeAllDynamicModals(root: TRootWithDynamicModals): void {
  // Snapshot: entries leave the array on `closed`, one frame later.
  const modals = [...(root._dynamicContainer?.modals ?? [])];
  for (const { modalAttrs } of modals) {
    if (modalAttrs?.name) root.$modal.hide(modalAttrs.name);
  }
}
